import React from "react";
import { UserRound, LogOut, KeyRound, Sparkles, Mail, Download, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import PasswordInput from "@/components/auth/PasswordInput";
import {
    fetchMe, registerAccount, loginAccount, logoutAccount, changePassword,
    changeEmail, updateDisplayName, exportAccount, deleteAccount,
} from "@/lib/api";

// FastAPI 422 returns an array of {msg,...}; map any detail shape to a string.
function formatApiErrorDetail(detail) {
    if (detail == null) return "";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

// Account section inside Settings. Guests can "claim their Mochi" (register)
// or sign in; signed-in users can rename themselves, change email/password
// or sign out. Auth changes reload the app so every widget refetches under
// the new identity.
export default function AccountSection({ open }) {
    const [me, setMe] = React.useState(null);          // null=loading, object=loaded
    const [mode, setMode] = React.useState(null);      // null | "register" | "login" | "password" | "email" | "name" | "delete"
    const [emailInput, setEmailInput] = React.useState("");
    const [nameInput, setNameInput] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [newPassword, setNewPassword] = React.useState("");
    const [deleteConfirm, setDeleteConfirm] = React.useState("");
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState("");

    React.useEffect(() => {
        if (!open) return;
        let cancelled = false;
        fetchMe()
            .then((d) => { if (!cancelled) setMe(d); })
            .catch(() => { if (!cancelled) setMe({ authenticated: false }); });
        return () => { cancelled = true; };
    }, [open]);

    const reloadSoon = () => setTimeout(() => window.location.reload(), 700);
    const fail = (err, fallback) => {
        setError(formatApiErrorDetail(err.response?.data?.detail) || fallback);
        setBusy(false);
    };

    const submitAuth = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            const cleanEmail = emailInput.trim().toLowerCase();
            const data = mode === "register"
                ? await registerAccount(cleanEmail, password, nameInput.trim())
                : await loginAccount(cleanEmail, password);
            toast.success(mode === "register"
                ? `welcome, ${data.display_name} 🍡 this Mochi is yours now`
                : `welcome back, ${data.display_name} 🍡`);
            reloadSoon();
        } catch (err) {
            fail(err, "something went sideways — try again?");
        }
    };

    const submitPassword = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            await changePassword(password, newPassword);
            toast.success("password updated 🌸");
            setMode(null); setPassword(""); setNewPassword(""); setBusy(false);
        } catch (err) {
            fail(err, "couldn't update password");
        }
    };

    const submitEmail = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            const data = await changeEmail(emailInput.trim().toLowerCase(), password);
            toast.success("sign-in email updated 🍡");
            setMe(data); setMode(null); setPassword(""); setEmailInput(""); setBusy(false);
        } catch (err) {
            fail(err, "couldn't update email");
        }
    };

    const submitName = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            const data = await updateDisplayName(nameInput.trim());
            toast.success(`okay, ${data.display_name} it is 🌸`);
            setMe(data); setMode(null); setNameInput(""); setBusy(false);
        } catch (err) {
            fail(err, "couldn't save that name");
        }
    };

    const handleExport = async () => {
        try {
            const data = await exportAccount();
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 10);
            a.href = url;
            a.download = `mochi-export-${stamp}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("export downloaded 🍡");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "export failed — try again?");
        }
    };

    const submitDelete = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            await deleteAccount(password, deleteConfirm);
            toast("account deleted — goodbye 🍃");
            setTimeout(() => window.location.reload(), 800);
        } catch (err) {
            fail(err, "couldn't delete — check password + type DELETE");
        }
    };

    const signOut = async () => {
        try { await logoutAccount(); } catch { /* cookies cleared regardless */ }
        toast("signed out — see you soon 🌸");
        reloadSoon();
    };

    const inputCls = "w-full bg-cream rounded-2xl px-4 py-2.5 font-body text-sm text-warm-900 outline-none border-2 border-transparent focus:border-terracotta transition-all";
    const openForm = (m) => {
        setMode(m); setError(""); setPassword(""); setNewPassword(""); setDeleteConfirm("");
        setEmailInput(m === "email" ? (me?.email || "") : "");
        setNameInput(m === "name" ? (me?.display_name || "") : "");
    };
    const CancelBtn = () => (
        <button type="button" onClick={() => openForm(null)}
            className="rounded-full bg-cream px-4 font-display text-sm text-warm-500">cancel</button>
    );
    const ErrorLine = () => error ? <p className="font-body text-xs text-terracotta" data-testid="account-error">{error}</p> : null;

    if (!me) return null;

    return (
        <div className="mb-5 rounded-clay bg-surface p-4" data-testid="account-section">
            <p className="font-display text-xs uppercase tracking-widest text-warm-500 mb-2 flex items-center gap-1">
                <UserRound size={12} /> Account
            </p>

            {me.authenticated ? (
                <>
                    <div className="flex items-center gap-3">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-terracotta-soft text-terracotta shrink-0">
                            <Sparkles size={17} />
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className="font-display text-base text-warm-900 leading-tight truncate" data-testid="account-status">
                                {me.display_name}
                            </p>
                            <p className="font-body text-[11px] text-warm-500 mt-0.5 truncate" data-testid="account-email-value" title={me.email || ""}>
                                {me.email}
                            </p>
                        </div>
                        <button
                            onClick={signOut}
                            className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-cream px-3 py-2 font-display text-xs text-warm-900 hover:bg-cream/70 transition-colors"
                            data-testid="account-logout-btn"
                        >
                            <LogOut size={13} /> Sign out
                        </button>
                    </div>

                    <div className="mt-3 flex flex-col items-start gap-2">
                    {/* ============ Display name ============ */}
                    {mode === "name" ? (
                        <form onSubmit={submitName} className="w-full space-y-2" data-testid="account-name-form">
                            <input type="text" required maxLength={40} value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                placeholder="what should mochi call you?"
                                autoCorrect="off" autoComplete="nickname"
                                className={inputCls} data-testid="account-display-name-input" />
                            <ErrorLine />
                            <div className="flex gap-2">
                                <button type="submit" disabled={busy}
                                    className="flex-1 rounded-full bg-terracotta text-cream font-display text-sm py-2 disabled:opacity-50 transition-opacity"
                                    data-testid="account-save-name-btn">
                                    {busy ? "saving…" : "Save name"}
                                </button>
                                <CancelBtn />
                            </div>
                        </form>
                    ) : (
                        <button onClick={() => openForm("name")}
                            className="inline-flex items-center gap-1.5 font-body text-[11px] text-warm-500 hover:text-warm-900 transition-colors"
                            data-testid="account-name-toggle">
                            <Pencil size={12} /> change what mochi calls you
                        </button>
                    )}

                    {/* ============ Password ============ */}
                    {mode === "password" ? (
                        <form onSubmit={submitPassword} className="w-full space-y-2" data-testid="account-password-form">
                            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)}
                                placeholder="current password" className={inputCls} testId="account-current-password-input" />
                            <PasswordInput minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="new password (6+ chars)" autoComplete="new-password" className={inputCls} testId="account-new-password-input" />
                            <ErrorLine />
                            <div className="flex gap-2">
                                <button type="submit" disabled={busy}
                                    className="flex-1 rounded-full bg-terracotta text-cream font-display text-sm py-2 disabled:opacity-50 transition-opacity"
                                    data-testid="account-change-password-btn">
                                    {busy ? "saving…" : "Update password"}
                                </button>
                                <CancelBtn />
                            </div>
                        </form>
                    ) : (
                        <button onClick={() => openForm("password")}
                            className="inline-flex items-center gap-1.5 font-body text-[11px] text-warm-500 hover:text-warm-900 transition-colors"
                            data-testid="account-password-toggle">
                            <KeyRound size={12} /> change password
                        </button>
                    )}

                    {/* ============ Sign-in email ============ */}
                    {mode === "email" ? (
                        <form onSubmit={submitEmail} className="w-full space-y-2" data-testid="account-email-form">
                            <input type="email" required value={emailInput}
                                onChange={(e) => setEmailInput(e.target.value)}
                                placeholder="new sign-in email"
                                autoCapitalize="none" autoCorrect="off" autoComplete="email" inputMode="email"
                                className={inputCls} data-testid="account-email-input" />
                            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)}
                                placeholder="confirm with current password"
                                autoComplete="current-password"
                                className={inputCls} testId="account-email-password-input" />
                            <ErrorLine />
                            <div className="flex gap-2">
                                <button type="submit" disabled={busy}
                                    className="flex-1 rounded-full bg-terracotta text-cream font-display text-sm py-2 disabled:opacity-50 transition-opacity"
                                    data-testid="account-save-email-btn">
                                    {busy ? "saving…" : "Update email"}
                                </button>
                                <CancelBtn />
                            </div>
                        </form>
                    ) : (
                        <button onClick={() => openForm("email")}
                            className="inline-flex items-center gap-1.5 font-body text-[11px] text-warm-500 hover:text-warm-900 transition-colors"
                            data-testid="account-email-toggle">
                            <Mail size={12} /> change sign-in email
                        </button>
                    )}
                    </div>

                    {/* ============ Data export ============ */}
                    <div className="mt-3 pt-3 border-t border-warm-300/30">
                        <button onClick={handleExport}
                            className="inline-flex items-center gap-1.5 font-body text-[11px] text-warm-500 hover:text-warm-900 transition-colors"
                            data-testid="account-export-btn">
                            <Download size={12} /> download my data (JSON)
                        </button>
                    </div>

                    {/* ============ Danger zone — delete account ============ */}
                    {mode === "delete" ? (
                        <form onSubmit={submitDelete} className="mt-3 space-y-2 rounded-2xl bg-terracotta-soft/30 border border-terracotta/40 p-3" data-testid="account-delete-form">
                            <p className="font-body text-xs text-warm-800 leading-relaxed">
                                this permanently deletes your mochi — every task, habit, reminder, list item, chat, and stats.
                                <span className="block mt-1 text-terracotta">there&apos;s no undo.</span>
                            </p>
                            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)}
                                placeholder="your current password"
                                autoComplete="current-password"
                                className={inputCls} testId="account-delete-password-input" />
                            <input type="text" required value={deleteConfirm}
                                onChange={(e) => setDeleteConfirm(e.target.value)}
                                placeholder="type DELETE to confirm"
                                autoCapitalize="characters" autoCorrect="off"
                                className={inputCls} data-testid="account-delete-confirm-input" />
                            <ErrorLine />
                            <div className="flex gap-2">
                                <button type="submit" disabled={busy}
                                    className="flex-1 rounded-full bg-terracotta text-cream font-display text-sm py-2 disabled:opacity-50 transition-opacity"
                                    data-testid="account-delete-confirm-btn">
                                    {busy ? "deleting…" : "Permanently delete"}
                                </button>
                                <CancelBtn />
                            </div>
                        </form>
                    ) : (
                        <button onClick={() => openForm("delete")}
                            className="mt-2 inline-flex items-center gap-1.5 font-body text-[11px] text-warm-500 hover:text-terracotta transition-colors"
                            data-testid="account-delete-toggle">
                            <Trash2 size={12} /> delete my account
                        </button>
                    )}
                </>
            ) : (
                <>
                    <div className="flex items-center gap-3">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-mustard-soft text-mustard shrink-0">
                            <UserRound size={17} />
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className="font-display text-base text-warm-900 leading-tight" data-testid="account-status">Guest mode</p>
                            <p className="font-body text-[11px] text-warm-500 mt-0.5">your data lives in this browser only</p>
                        </div>
                    </div>

                    {mode === "register" || mode === "login" ? (
                        <form onSubmit={submitAuth} className="mt-3 space-y-2" data-testid="account-auth-form">
                            <input type="email" required maxLength={254} value={emailInput}
                                onChange={(e) => setEmailInput(e.target.value)} placeholder="email"
                                autoCapitalize="none" autoCorrect="off" inputMode="email"
                                autoComplete={mode === "register" ? "email" : "username"}
                                className={inputCls} data-testid="account-email-input" />
                            {mode === "register" && (
                                <input type="text" maxLength={40} value={nameInput}
                                    onChange={(e) => setNameInput(e.target.value)}
                                    placeholder="what should mochi call you? (optional)"
                                    autoCorrect="off" autoComplete="nickname"
                                    className={inputCls} data-testid="account-register-name-input" />
                            )}
                            <PasswordInput minLength={mode === "register" ? 6 : 1} value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={mode === "register" ? "password (6+ chars)" : "password"}
                                autoComplete={mode === "register" ? "new-password" : "current-password"}
                                className={inputCls} testId="account-password-input" />
                            <ErrorLine />
                            <div className="flex gap-2">
                                <button type="submit" disabled={busy}
                                    className="flex-1 rounded-full bg-terracotta text-cream font-display text-sm py-2 disabled:opacity-50 transition-opacity"
                                    data-testid={mode === "register" ? "account-register-btn" : "account-login-btn"}>
                                    {busy ? "one sec…" : mode === "register" ? "Claim your Mochi 🍡" : "Sign in"}
                                </button>
                                <CancelBtn />
                            </div>
                            <button type="button"
                                onClick={() => openForm(mode === "register" ? "login" : "register")}
                                className="font-body text-[11px] text-warm-500 hover:text-warm-900 transition-colors"
                                data-testid="account-mode-toggle">
                                {mode === "register" ? "already have an account? sign in" : "new here? claim your Mochi instead"}
                            </button>
                        </form>
                    ) : (
                        <div className="mt-3 flex gap-2">
                            <button onClick={() => openForm("register")}
                                className="flex-1 rounded-full bg-terracotta text-cream font-display text-sm py-2.5 hover:opacity-90 transition-opacity"
                                data-testid="account-open-register-btn">
                                Claim your Mochi 🍡
                            </button>
                            <button onClick={() => openForm("login")}
                                className="flex-1 rounded-full bg-cream font-display text-sm text-warm-900 py-2.5 hover:bg-cream/70 transition-colors"
                                data-testid="account-open-login-btn">
                                Sign in
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
